import { cn } from '@/lib/utils';
import {
	PRONTUARIO_SESSION_DETAIL_LABEL_CLASS,
	PRONTUARIO_SESSION_DETAIL_VALUE_CLASS,
} from '../constants/prontuarioTheme';
import {
	formatProntuarioSessionChoiceLabel,
	getProntuarioSessionChoiceOptions,
	type ProntuarioSessionChoiceValue,
	type ProntuarioSessionFieldKind,
} from '../constants/prontuarioSessionChoices';

export const PRONTUARIO_EDITABLE_LABEL_CLASSNAME =
	'text-xs font-semibold text-slate-200';

export const PRONTUARIO_READONLY_LABEL_CLASSNAME =
	'text-xs font-semibold uppercase tracking-wide text-slate-500';

/** @deprecated use PRONTUARIO_EDITABLE_LABEL_CLASSNAME */
export const PRONTUARIO_LABEL_CLASSNAME = PRONTUARIO_EDITABLE_LABEL_CLASSNAME;

export const PRONTUARIO_READONLY_VALUE_CLASSNAME = cn(
	'mt-1.5 rounded-md border border-dashed border-slate-700 bg-slate-950/40 px-3 py-2.5',
	'text-sm leading-relaxed text-slate-50 cursor-default',
);

export const PRONTUARIO_INPUT_CLASSNAME = cn(
	'w-full rounded-lg border border-slate-500 bg-slate-800 px-3 py-2.5',
	'text-sm text-slate-50 placeholder:text-slate-500',
	'outline-none transition-colors',
	'focus:border-slate-300 focus:ring-2 focus:ring-white/15',
);

export const PRONTUARIO_SECTION_CARD_CLASSNAME =
	'rounded-lg border border-slate-600/70 bg-slate-800/60 p-4';

type ProntuarioFieldAppearance = 'default' | 'material';

type ProntuarioReadonlyFieldProps = {
	label: string;
	value: string;
	fullWidth?: boolean;
	appearance?: ProntuarioFieldAppearance;
};

const ProntuarioReadonlyField = ({
	label,
	value,
	fullWidth = false,
	appearance = 'default',
}: ProntuarioReadonlyFieldProps) => (
	<div className={cn('min-w-0', fullWidth && 'sm:col-span-2')}>
		<p
			className={
				appearance === 'material'
					? PRONTUARIO_SESSION_DETAIL_LABEL_CLASS
					: PRONTUARIO_READONLY_LABEL_CLASSNAME
			}
		>
			{label}
		</p>
		<p
			aria-readonly
			className={cn(
				appearance === 'material'
					? PRONTUARIO_SESSION_DETAIL_VALUE_CLASS
					: PRONTUARIO_READONLY_VALUE_CLASSNAME,
				!fullWidth && 'truncate',
				fullWidth && 'break-words',
			)}
		>
			{value}
		</p>
	</div>
);

type ProntuarioTextFieldProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
};

const ProntuarioTextField = ({
	label,
	value,
	onChange,
	placeholder,
}: ProntuarioTextFieldProps) => (
	<div className="min-w-0 space-y-1.5">
		<label className={PRONTUARIO_EDITABLE_LABEL_CLASSNAME}>{label}</label>
		<input
			type="text"
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder={placeholder}
			className={PRONTUARIO_INPUT_CLASSNAME}
		/>
	</div>
);

type ProntuarioTextareaFieldProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	rows?: number;
};

const ProntuarioTextareaField = ({
	label,
	value,
	onChange,
	placeholder,
	rows = 2,
}: ProntuarioTextareaFieldProps) => (
	<div className="min-w-0 space-y-1.5">
		<label className={PRONTUARIO_EDITABLE_LABEL_CLASSNAME}>{label}</label>
		<textarea
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder={placeholder}
			rows={rows}
			className={cn(
				PRONTUARIO_INPUT_CLASSNAME,
				'resize-none leading-relaxed',
			)}
		/>
	</div>
);

const PRONTUARIO_CHOICE_SELECTED_CLASSNAME =
	'border-white/20 bg-white/[0.08] text-slate-50';

type ProntuarioReadonlyChoiceFieldProps = {
	label: string;
	value: ProntuarioSessionChoiceValue;
	kind: Exclude<ProntuarioSessionFieldKind, 'text'>;
	fullWidth?: boolean;
	appearance?: ProntuarioFieldAppearance;
};

const ProntuarioReadonlyChoiceField = ({
	label,
	value,
	kind,
	fullWidth = false,
	appearance = 'default',
}: ProntuarioReadonlyChoiceFieldProps) => {
	const options = getProntuarioSessionChoiceOptions(kind);

	return (
		<div className={cn('min-w-0', fullWidth && 'sm:col-span-2')}>
			<p
				className={
					appearance === 'material'
						? PRONTUARIO_SESSION_DETAIL_LABEL_CLASS
						: PRONTUARIO_READONLY_LABEL_CLASSNAME
				}
			>
				{label}
			</p>
			<div className="mt-1.5 flex flex-wrap gap-1.5">
				{options.map((option) => {
					const isSelected = option.value === value;

					return (
						<span
							key={option.value}
							aria-current={isSelected ? 'true' : undefined}
							className={cn(
								'rounded-md px-2.5 py-1 text-sm font-medium',
								appearance === 'material'
									? isSelected
										? 'bg-white/[0.1] text-slate-200'
										: 'bg-white/[0.03] text-slate-600'
									: cn(
											'border',
											isSelected
												? PRONTUARIO_CHOICE_SELECTED_CLASSNAME
												: 'border-transparent bg-white/[0.03] text-slate-600',
									  ),
							)}
						>
							{option.label}
						</span>
					);
				})}
			</div>
		</div>
	);
};

type ProntuarioChoiceFieldProps = {
	label: string;
	value: ProntuarioSessionChoiceValue | '';
	kind: Exclude<ProntuarioSessionFieldKind, 'text'>;
	onChange: (value: ProntuarioSessionChoiceValue) => void;
	fullWidth?: boolean;
};

const ProntuarioChoiceField = ({
	label,
	value,
	kind,
	onChange,
	fullWidth = false,
}: ProntuarioChoiceFieldProps) => {
	const options = getProntuarioSessionChoiceOptions(kind);

	return (
		<div
			className={cn('min-w-0 space-y-1.5', fullWidth && 'sm:col-span-2')}
		>
			<p className={PRONTUARIO_EDITABLE_LABEL_CLASSNAME}>{label}</p>
			<div className="flex flex-wrap gap-1.5">
				{options.map((option) => {
					const isSelected = value === option.value;

					return (
						<button
							key={option.value}
							type="button"
							aria-pressed={isSelected}
							onClick={() => onChange(option.value)}
							className={cn(
								'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
								isSelected
									? PRONTUARIO_CHOICE_SELECTED_CLASSNAME
									: 'border-slate-600 bg-slate-800/60 text-slate-400 hover:border-slate-500 hover:text-slate-200',
							)}
						>
							{option.label}
						</button>
					);
				})}
			</div>
		</div>
	);
};

export {
	ProntuarioChoiceField,
	ProntuarioReadonlyChoiceField,
	ProntuarioReadonlyField,
	ProntuarioTextField,
	ProntuarioTextareaField,
	formatProntuarioSessionChoiceLabel,
};
